import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { exportA5MultiAsPdf, exportStaticAsPng, EXPORT_WIDTHS, smartDownloadBlob, isIOSDevice } from "@/lib/staticExport";
import { copyToClipboard } from "@/lib/clipboard";
import StaticTemplatePreview, { LAYOUTS, COLORS, BrochureCover } from "@/components/StaticTemplatePreview";
import { api } from "@/lib/api";

/**
 * Anteprima pubblica del nuovo template `volantino_stima` (A5 fronte/retro).
 *
 * Mostra tutte le combinazioni di Layout × Colore con dati di esempio
 * popolati come arriverebbero dal profilo dell'agente in produzione.
 * Pagina temporanea — al deploy verrà rimossa.
 */
export default function VolantinoStimaAnteprima() {
  const [layout, setLayout] = useState("elegante");
  const [color, setColor] = useState("verde_brera");
  const [quality, setQuality] = useState("print"); // "print" 600 DPI · "web" 150 DPI
  const [exporting, setExporting] = useState(null);
  const [withLogo, setWithLogo] = useState(false);
  const [agentManifest, setAgentManifest] = useState(null); // dati REALI dell'agente loggato
  const previewRef = useRef(null);

  // Se l'agente è loggato, fetch i suoi dati veri. Se 401, restiamo in modalità demo.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/mandates/flyer-manifest");
        if (!cancel && data?.agency) setAgentManifest(data);
      } catch {
        // not logged → demo mode
      }
    })();
    return () => { cancel = true; };
  }, []);

  const isReal = !!agentManifest;

  const property = {
    title: "Stima Brera",
    city: "Brera · Milano",
    photos: [],
  };
  const demoEstimateUrl = "https://studioverzi.it/stima";
  const demoAgency = {
    name: "Studio Verzi",
    web: "studioverzi.it",
    address: "Via Solferino 12, Milano",
    agent_name: "Marco Verzi",
    agent_phone: "+39 348 12 34 567",
    agent_email: "marco@studioverzi.it",
    agent_license: "FIMAA MI · A.2.1985",
    area_short: "Brera · Centro",
    estimate_url_short: "studioverzi.it/stima",
    estimate_url_full: demoEstimateUrl,
    landing_qr_url: `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(demoEstimateUrl)}&size=200x200&margin=0&format=png`,
    estimate_qr_url: `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(demoEstimateUrl)}&size=200x200&margin=0&format=png`,
    agent_photo: null,
    logo_url: withLogo
      ? "data:image/svg+xml;utf8," + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 80"><rect width="200" height="80" fill="#1C1C1A"/><text x="100" y="42" font-family="Georgia,serif" font-style="italic" font-size="28" fill="#FAFAF7" text-anchor="middle">Studio Verzi</text><line x1="40" y1="56" x2="160" y2="56" stroke="#7C8B6E" stroke-width="1"/><text x="100" y="68" font-family="Helvetica,Arial,sans-serif" font-size="7" letter-spacing="3" fill="#7C8B6E" text-anchor="middle">SINCE 2001</text></svg>`,
      )
      : null,
  };

  // Se loggato → uso i DATI VERI dell'agente (incluso logo da profilo).
  const agency = isReal ? agentManifest.agency : demoAgency;
  const estimateUrl = agency.estimate_url_full || demoEstimateUrl;

  const copyLink = async () => {
    // iter 313 r20 — clipboard robusta con fallback iOS (execCommand + Web Share).
    // navigator.clipboard.writeText() falliva silenziosamente su Safari mobile.
    const r = await copyToClipboard(estimateUrl, { shareTitle: "Link stima" });
    if (r === "clipboard") toast.success("Link copiato! Incollalo nella story IG come sticker.");
    else if (r === "share") { /* OS share-sheet ha gestito */ }
    else toast.info("Tocca a lungo per copiare:", { description: estimateUrl, duration: 10000 });
  };

  // iter 313 r21 — preset qualità: stampa 600 DPI (max) o web 150 DPI (~1080px IG).
  const QUALITY_WIDTH = quality === "print" ? EXPORT_WIDTHS.volantino_stima : 1080;
  const QUALITY_JPEG = quality === "print" ? 0.98 : 0.85;

  const downloadPng = async (which) => {
    if (!previewRef.current) return;
    setExporting(`png-${which}`);
    try {
      const pages = previewRef.current.querySelectorAll('[data-testid="brochure-page"]');
      const node = which === "fronte" ? pages[0] : pages[1];
      if (!node) throw new Error("Pagina non trovata");
      await exportStaticAsPng({
        node,
        targetWidth: QUALITY_WIDTH,
        filename: `volantino-stima-${which}-${color}-${quality}.png`,
      });
      toast.success(`PNG ${which} scaricato — pronto per Instagram.`);
    } catch (err) {
      console.error(err);
      toast.error("Export fallito. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  const downloadPdf = async () => {
    if (!previewRef.current) return;
    setExporting("pdf");
    try {
      const pages = Array.from(previewRef.current.querySelectorAll('[data-testid="brochure-page"]'));
      if (pages.length !== 2) throw new Error("Volantino non pronto");
      const filename = `volantino-stima-${color}-${quality}.pdf`;
      // iter 313 r20 — bug fix iOS: `pdf.save()` (autoSave:true) NON funziona
      // su Safari mobile (file scaricato ma non visibile in Download). Genero
      // il blob e passo a smartDownloadBlob → share-sheet su iOS (Allega in
      // File / WhatsApp / Mail), download classico su desktop/Android.
      // iter 313 r21 — qualità configurabile (print/web).
      const pdfBlob = await exportA5MultiAsPdf({
        pageNodes: pages,
        filename,
        autoSave: false,
        targetWidth: QUALITY_WIDTH,
        jpegQuality: QUALITY_JPEG,
      });
      await smartDownloadBlob(pdfBlob, filename, "application/pdf");
      if (isIOSDevice()) {
        toast.success("Volantino pronto. Tocca 'Condividi' per salvarlo in File o WhatsApp.");
      } else {
        toast.success("PDF scaricato — pronto per la stampa.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Export PDF fallito. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `Ciao, curioso di sapere quanto vale la tua casa? Te lo dico in 30 secondi. Niente nome, niente numero di telefono, 100% anonimo: ${estimateUrl}`,
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#1C1C1A] text-white py-10 px-5">
      <div className="max-w-6xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#A8B89A] font-semibold mb-1">Anteprima · fac-simile</div>
        <h1 className="font-editorial text-3xl sm:text-4xl mb-3" style={{ fontFamily: "Georgia, serif" }}>
          Volantino {"\"Stima casa\""} · A5 fronte/retro
        </h1>
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-5 text-[12.5px] text-amber-100 leading-snug">
          <strong className="text-amber-200">Tutti i dati sono inventati</strong> (Studio Verzi, Marco Verzi, Via Solferino, telefono, email, indirizzo, licenza).
          In produzione vengono prelevati dal <strong>tuo profilo</strong>: nome agenzia, agente, foto, telefono, email, indirizzo, licenza FIMAA/FIAIP, link personale.
        </div>
        <p className="text-[14px] text-[#D6D3CB] max-w-2xl leading-relaxed mb-7">
          Stesso engine delle brochure A4 (4 stili × 8 colori). Brand bianco totale:
          nessun riferimento a Pronto. Solo nome agenzia + agente + telefono/email + QR.
          Cambia stile e colore qui sotto per vedere le varianti.
        </p>

        {/* Selettori */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-2">Stile</div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(LAYOUTS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => setLayout(k)}
                    className={`px-3 py-2 rounded-md text-[12px] font-semibold ${layout === k ? "bg-white text-[#1C1C1A]" : "bg-white/10 text-white hover:bg-white/20"}`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-2">Colore</div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(COLORS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => setColor(k)}
                    title={v.name}
                    className={`w-9 h-9 rounded-full border-2 ${color === k ? "border-white" : "border-white/20"}`}
                    style={{ background: v.swatch }}
                    aria-label={v.name}
                  />
                ))}
              </div>
              <div className="text-[11px] text-[#A8B89A] mt-2">Colore attivo: <strong className="text-white">{COLORS[color]?.name}</strong></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-2">Logo agenzia</div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setWithLogo(false)} className={`px-3 py-2 rounded-md text-[12px] font-semibold ${!withLogo ? "bg-white text-[#1C1C1A]" : "bg-white/10 text-white hover:bg-white/20"}`}>Solo testo</button>
                <button onClick={() => setWithLogo(true)} className={`px-3 py-2 rounded-md text-[12px] font-semibold ${withLogo ? "bg-white text-[#1C1C1A]" : "bg-white/10 text-white hover:bg-white/20"}`}>Con logo image</button>
              </div>
            </div>
          </div>
        </div>

        {/* Anteprima */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-8" ref={previewRef}>
          <StaticTemplatePreview
            template="volantino_stima"
            layout={layout}
            color={color}
            property={property}
            agency={agency}
          />
        </div>

        {/* ── Pubblica / scarica ───────────────────────────────── */}
        <div className="bg-white text-[#1C1C1A] rounded-2xl p-5 sm:p-7 mt-6" data-testid="vstim-publish">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold mb-1">
            Pubblica · Stampa · Condividi
          </div>
          <h3 className="font-editorial text-2xl mb-4" style={{ fontFamily: "Georgia, serif" }}>
            Porta il volantino in giro
          </h3>

          {/* Link copiabile big */}
          <div className="bg-[#FAF9F5] border border-[#EAE7DF] rounded-lg p-4 mb-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-bold mb-1.5">
              Link diretto alla stima
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[14px] sm:text-[15px] font-mono text-[#1C1C1A] flex-1 min-w-[200px] break-all select-all">
                {estimateUrl}
              </code>
              <button
                onClick={copyLink}
                className="px-3 py-1.5 rounded-md bg-[#1C1C1A] text-white text-[12px] font-semibold hover:bg-black"
                data-testid="vstim-copy-link"
              >
                Copia link
              </button>
            </div>
            <p className="text-[11.5px] text-[#5A584F] mt-2 leading-snug">
              Usalo nella <strong>bio Instagram</strong>, nel <strong>sticker Link</strong> delle story (così è davvero cliccabile),
              nella caption dei post, nei messaggi WhatsApp e nella firma email.
            </p>
          </div>

          {/* iter 313 r21 — Selettore qualità: Stampa 600 DPI vs Web 150 DPI */}
          <div className="bg-[#FAF9F5] border border-[#EAE7DF] rounded-lg p-3 mb-3" data-testid="vstim-quality-selector">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-bold mb-2">
              Qualità del file
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setQuality("print")}
                className={`text-left p-2.5 rounded-md border-2 transition-all ${
                  quality === "print"
                    ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                    : "border-[#EAE7DF] hover:border-[#7C8B6E] bg-white text-[#1C1C1A]"
                }`}
                data-testid="vstim-quality-print"
              >
                <div className="text-[11.5px] font-bold">Stampa · 600 DPI</div>
                <div className={`text-[10.5px] mt-0.5 leading-snug ${quality === "print" ? "text-white/70" : "text-[#6B6A65]"}`}>
                  Massima risoluzione · ~1,5-4 MB
                </div>
              </button>
              <button
                type="button"
                onClick={() => setQuality("web")}
                className={`text-left p-2.5 rounded-md border-2 transition-all ${
                  quality === "web"
                    ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                    : "border-[#EAE7DF] hover:border-[#7C8B6E] bg-white text-[#1C1C1A]"
                }`}
                data-testid="vstim-quality-web"
              >
                <div className="text-[11.5px] font-bold">Web · 150 DPI</div>
                <div className={`text-[10.5px] mt-0.5 leading-snug ${quality === "web" ? "text-white/70" : "text-[#6B6A65]"}`}>
                  Leggero · ~200-400 KB · ottimo per WhatsApp/IG
                </div>
              </button>
            </div>
          </div>

          {/* 3 download buttons */}
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <button
              onClick={() => downloadPng("fronte")}
              disabled={exporting !== null}
              className="px-4 py-3 rounded-lg bg-[#7C8B6E] text-white font-semibold text-[13.5px] hover:bg-[#6A7860] disabled:opacity-60 text-left"
              data-testid="vstim-png-fronte"
            >
              <div className="text-[10px] uppercase tracking-wider opacity-80">Per Instagram Story</div>
              <div>{exporting === "png-fronte" ? "Genero…" : "Scarica fronte · PNG"}</div>
            </button>
            <button
              onClick={() => downloadPng("retro")}
              disabled={exporting !== null}
              className="px-4 py-3 rounded-lg bg-[#7C8B6E] text-white font-semibold text-[13.5px] hover:bg-[#6A7860] disabled:opacity-60 text-left"
              data-testid="vstim-png-retro"
            >
              <div className="text-[10px] uppercase tracking-wider opacity-80">Per Instagram Story</div>
              <div>{exporting === "png-retro" ? "Genero…" : "Scarica retro · PNG"}</div>
            </button>
            <button
              onClick={downloadPdf}
              disabled={exporting !== null}
              className="px-4 py-3 rounded-lg bg-[#1C1C1A] text-white font-semibold text-[13.5px] hover:bg-black disabled:opacity-60 text-left"
              data-testid="vstim-pdf"
            >
              <div className="text-[10px] uppercase tracking-wider opacity-80">Per stampa</div>
              <div>{exporting === "pdf" ? "Genero…" : "Scarica PDF A5 fronte/retro"}</div>
            </button>
          </div>

          <button
            onClick={shareWhatsApp}
            className="w-full px-4 py-3 rounded-lg bg-[#25D366] text-white font-semibold text-[13.5px] hover:bg-[#20BA5A]"
            data-testid="vstim-whatsapp"
          >
            Invia il link su WhatsApp
          </button>

          {/* Note "come si fa il QR cliccabile sui social" */}
          <div className="mt-5 pt-5 border-t border-[#EAE7DF]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold mb-2">
              Trucco: come renderlo cliccabile su Instagram
            </div>
            <ol className="space-y-1.5 text-[12.5px] text-[#3F3D38] leading-snug list-decimal pl-5">
              <li>Scarica il PNG e caricalo come <strong>Story Instagram</strong>.</li>
              <li>Tocca l{"'"}icona sticker (faccina in alto) → <strong>Link</strong> → incolla il link che hai copiato sopra.</li>
              <li>Posiziona lo sticker sul QR del volantino. Ora chi tocca il QR <strong>apre direttamente la stima</strong>.</li>
            </ol>
          </div>
        </div>

        <p className="text-[11.5px] text-[#A8B89A] mt-5 italic">
          Anteprima dimostrativa: in produzione, le info agente sono prelevate dal profilo (foto, telefono, email, agenzia, indirizzo, licenza).
          Il link sarà <code>tuo-dominio.it/stima</code> oppure <code>prontoimmo.it/p/tuo-slug</code> a seconda del piano.
        </p>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5 mt-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#A8B89A] font-semibold mb-2">Logo agenzia · formato consigliato</div>
          <ul className="text-[13px] text-[#D6D3CB] leading-relaxed list-disc pl-5 space-y-1">
            <li><strong className="text-white">PNG con sfondo trasparente</strong> (consigliato) oppure SVG / JPG su sfondo bianco</li>
            <li><strong className="text-white">Risoluzione consigliata: 800×400px</strong> (rapporto 2:1) oppure quadrato 600×600px</li>
            <li><strong className="text-white">Peso massimo: 1 MB</strong></li>
            <li>Quando il fondo del volantino è scuro, il logo viene <strong className="text-white">invertito automaticamente</strong> in bianco (per questo è meglio fornirlo monocromo / scuro su sfondo trasparente)</li>
            <li>Senza logo, viene usato un <strong className="text-white">lockup tipografico</strong> col nome agenzia nel font del layout (visibile qui in modalità {"\"Solo testo\""})</li>
          </ul>
          <p className="text-[11.5px] text-[#A8B89A] mt-3 italic">
            Carichi una sola volta nel tuo profilo → viene riusato in: volantini, brochure, story IG, email firma, landing personale, kit mandati.
          </p>
        </div>

        {/* ── Confronto colori brochure ─────────────────────────── */}
        <div className="mt-14 pt-10 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#A8B89A] font-semibold mb-1">Riferimento</div>
          <h2 className="font-editorial text-2xl sm:text-3xl mb-3" style={{ fontFamily: "Georgia, serif" }}>
            Brochure attuali · cover in tutti i colori
          </h2>
          <p className="text-[13.5px] text-[#D6D3CB] max-w-2xl leading-relaxed mb-7">
            Stesso engine che useremo per il volantino. Ecco le 8 palette esistenti: <strong className="text-white">dimmi quali tenere</strong>.
            Il commento originale del codice diceva {"\"4 colori: scuro, cremino, bianco, verde_brera\""} ma sono stati estesi a 8 in seguito.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(COLORS).map(([key, col]) => (
              <div key={key}>
                <div className="aspect-[210/297] rounded-sm overflow-hidden relative shadow-2xl">
                  <BrochureCover
                    layout="elegante"
                    color={key}
                    property={property}
                    agency={agency}
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full" style={{ background: col.swatch }} />
                  <span className="text-[12px] font-semibold text-white">{col.name}</span>
                  <span className="text-[10px] text-[#A8B89A] font-mono ml-auto">{key}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11.5px] text-[#A8B89A] mt-5 italic">
            Nota: queste sono le cover Brochure A4 (4 pagine), in stile Elegante. Ogni colore funziona anche su Luxury / Moderno / Minimal.
          </p>
        </div>
      </div>
    </div>
  );
}
